<template>
    <div v-if="isLoading" class="py-4 container-fluid p-5 text-center">
        <div class="row">
            <div class="card p-10">
                <div class="spinner-border text-success" role="status">
                    <span class="visually-hidden">Cargando...</span>
                </div>
            </div>
        </div>
    </div>
    <div v-else class="container-fluid py-4">
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header pb-0">
                        <div class="row">
                            <div class="col-12 col-md-6">
                                <h6>Detalle de Factura</h6>
                                <p class="text-sm mb-0">{{factura.header.invoice_type}} #{{ factura.header.full_number }}</p>
                            </div>

                            <div class="col-12 col-md-2 text-md-end mt-2 mt-md-0">
                                <argon-button
                                class="btn btn-success w-100 w-md-auto"
                                @click="printPDF"
                                size="sm"
                                >
                                <i class="fa fa-print me-1"></i>
                                Imprimir
                                </argon-button>
                            </div>

                            <div class="col-12 col-md-2 text-md-end mt-2 mt-md-0">
                                <argon-button
                                v-if="canInvalidate"
                                class="btn btn-danger w-100 w-md-auto"
                                @click="handleInvalidationFlow"
                                size="sm"
                                :disabled="isSendingInvalidation"
                                >
                                <i class="fa fa-times me-1"></i>
                                {{ isSendingInvalidation ? 'Anulando...' : 'Anular' }}
                                </argon-button>
                            </div>

                            <div class="col-12 col-md-2 text-md-end mt-2 mt-md-0">
                                <argon-button
                                class="btn btn-success w-100 w-md-auto"
                                @click="goBack"
                                size="sm"
                                >
                                <i class="fa fa-arrow-left me-1"></i>
                                Volver
                                </argon-button>
                            </div>
                        </div>

                    </div>
                    <div class="card-body pb-2">
                        <div class="row mb-4">
                            <div class="col-md-6">
                                <h6 class="mb-2">Información de la Empresa</h6>
                                <p class="text-sm mb-1"><strong>NIT:</strong> {{ factura.header.emitter_nit }}</p>
                                <p class="text-sm mb-1"><strong>Nombre:</strong> {{ factura.header.emitter_name }}</p>
                            </div>
                            <div class="col-md-6">
                                <h6 class="mb-2">Información del Cliente</h6>
                                <p class="text-sm mb-1"><strong>NIT:</strong> {{ factura.header.client_nit }}</p>
                                <p class="text-sm mb-1"><strong>Nombre:</strong> {{ factura.header.client_name }}</p>
                                <p class="text-sm mb-1"><strong>Tipo:</strong> {{ factura.header.client_type || 'N/A' }}</p>
                            </div>
                        </div>

                        <div class="row mb-4">
                            <div class="col-md-6">
                                <p class="text-sm mb-1"><strong>Tipo:</strong> {{ factura.header.invoice_type === 'FACTURA' ? 'Factura' : 'Ticket' }}</p>
                                <p class="text-sm mb-1"><strong>Fecha de Emisión:</strong> {{ $formatDate(factura.header.date_issued) }}</p>
                                <p class="text-sm mb-1"><strong>Moneda:</strong> {{ factura.header.currency }}</p>
                                <p class="text-sm mb-1">
                                    <strong>Estado: </strong>
                                    <span :class="getStateClass(factura.header.state)">
                                        {{ getStateText(factura.header.state) }}
                                    </span>
                                </p>
                            </div>
                            <div class="col-md-6 text-end">
                                <p class="text-sm mb-1"><strong>Serie/Número:</strong> {{ factura.header.series }}-{{ factura.header.number }}</p>
                            </div>
                        </div>

                        <div class="table-responsive">
                            <table class="table mb-0">
                                <thead>
                                    <tr>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Descripción</th>
                                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cantidad</th>
                                        <th class="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Precio Unit.</th>
                                        <th class="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-danger">Descuento</th>
                                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Impuesto (%)</th>
                                        <th class="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total Línea</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(line, index) in factura.lines" :key="index">
                                        <td class="text-start">
                                            <p class="text-xs font-weight-bold mb-0">{{ line.description }}</p>
                                        </td>
                                        <td class="text-center">
                                            <p class="text-xs font-weight-bold mb-0">{{ line.quantity.toFixed(2) }}</p>
                                        </td>
                                        <td class="text-end">
                                            <p class="text-xs font-weight-bold mb-0">
                                                {{ $formatCurrency(line.unit_price, currency, symbol) }}
                                            </p>
                                        </td>
                                        <td class="text-end text-danger">
                                            <p class="text-xs font-weight-bold mb-0">
                                                {{ $formatCurrency(line.discount, currency, symbol) }}
                                            </p>
                                        </td>
                                        <td class="text-end">
                                            <p class="text-xs font-weight-bold mb-0">{{$formatCurrency(line.tax_rate.toFixed(2), currency, symbol) }}</p>
                                        </td>
                                        <td class="text-end">
                                            <p class="text-xs font-weight-bold mb-0">
                                                {{ $formatCurrency(line.line_total, currency, symbol) }}
                                            </p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="row mt-4">
                            <div class="col-md-6"></div>
                            <div class="col-md-6">
                                <div class="table-responsive">
                                    <table class="table mb-0">
                                        <tbody>
                                            <tr>
                                                <td class="text-start"><strong>Subtotal Bruto:</strong></td>
                                                <td class="text-end">
                                                    {{ $formatCurrency(factura.header.sub_total_brut, currency, symbol) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="text-start text-danger"><strong>Descuento Total:</strong></td>
                                                <td class="text-end text-danger">
                                                    {{ $formatCurrency((factura.header.total_discount*-1), currency, symbol) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="text-start"><strong>Subtotal Neto (Base Imp.):</strong></td>
                                                <td class="text-end">
                                                    {{ $formatCurrency(factura.header.subtotal, currency, symbol) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="text-start"><strong>Impuestos:</strong></td>
                                                <td class="text-end">
                                                    {{ $formatCurrency(factura.header.total_taxes, currency, symbol) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="text-start"><strong>Retención:</strong></td>
                                                <td class="text-end text-danger">
                                                    {{ $formatCurrency((factura.header.retention_amount*-1), currency, symbol) }}
                                                </td>
                                            </tr>
                                            <tr class="font-weight-bold">
                                                <td class="text-start"><strong>Total Final:</strong></td>
                                                <td class="text-end text-success">
                                                    {{ $formatCurrency(factura.header.total, currency, symbol) }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div class="row mt-3" v-if="factura.header.dte_trace_code">
                            <div class="col-12">
                                <h6 class="mb-2">Información DTE (Facturación Electrónica)</h6>
                                <p class="text-sm mb-1" v-if="factura.header.dte_trace_code">
                                    <strong>Código de Generación:</strong> {{ factura.header.dte_trace_code }}
                                </p>
                                <p class="text-sm mb-1" v-if="factura.header.dte_stamp_code">
                                    <strong>Sello Recibido:</strong> {{ factura.header.dte_stamp_code }}
                                </p>
                                <p class="text-sm mb-1" v-if="factura.header.dte_status">
                                    <strong>Estado MH:</strong>
                                    <span :class="{'text-danger': factura.header.dte_status === 'RECHAZADO', 'text-info': factura.header.dte_status === 'PENDIENTE_ANULACION', 'text-success': factura.header.dte_status === 'CERTIFICADO'}">
                                        {{ factura.header.dte_status }}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// Importa ArgonButton si lo usas
import ArgonButton from "@/components/ArgonButton.vue";
import { useInvoicePrinter } from "@/composables/useInvoicePrinterPOS";
import { useDTEFiscal } from "@/composables/useDTEFiscal";
import { useCompanyConfig } from '@/composables/useCompanyConfig';

import axios from "axios";
export default {
    name: "factura-detail",
    components: {
        ArgonButton,
    },
    setup(){
        const { companyConfig, isLoadingCiaConfig, fetchCompanyConfig } = useCompanyConfig();
        fetchCompanyConfig();
        return { companyConfig ,isLoadingCiaConfig};
    },
    data() {
        return {
            isLoading: true,
            isSendingInvalidation: false, // Nuevo estado de envío
            factura: null,
            currency: 'USD',
            symbol: '$',
            currentUser: null, // Datos del usuario logueado (necesarios para el motivo de anulación)
        };
    },
    computed: {
        /**
         * Verifica si el documento es elegible para anulación (Invalidación MH).
         * Reglas: Debe estar certificado (tener sello/código MH) y no debe estar ya anulado/cancelado.
         */
        canInvalidate() {
            if (!this.factura || !this.factura.header) {
                return false;
            }
            const header = this.factura.header;

            // 1. Debe haber sido enviado a MH y certificado
            const isDteCertified = header.dte_status === 'CERTIFICADO' && header.dte_trace_code && header.dte_stamp_code;

            // 2. No debe estar ya cancelado internamente ni en proceso de anulación
            const isNotAnnulled = header.state !== 'CANCELLED' &&
                                  header.dte_status !== 'ANULADO' &&
                                  header.dte_status !== 'PENDIENTE_ANULACION';

            // 3. Documentos que se anulan por este proceso (01, 03, etc.)
            const isAnnulableDte = ['01', '03', '14'].includes(header.dte_type);

            return isDteCertified && isNotAnnulled && isAnnulableDte;
        }
    },
    watch: {
        companyConfig(newVal){
            this.currency = newVal.currency_code;
            this.symbol = newVal.currency_symbol;
        },
    },
    mounted() {
        // Cargar usuario desde localStorage para los datos de responsable/solicitante
        try {
            const userData = {
                fullName:localStorage.getItem('userFullName'),
                nit:localStorage.getItem('userDocument')

            };
            this.currentUser = userData;
        } catch (e) {
            console.error("No se pudo cargar el usuario de localStorage.", e);
        }

        const token = localStorage.getItem("accessToken");
        const config = {
            headers: {
                'Content-Type': 'application/json',
                'accept': '*/*',
                'Authorization':`Bearer ${token}`
            }
        };
        this.fetchInvoice(config);
    },
    methods: {
        async fecthCustomer(config){
            const self = this;
            axios.get(`${this.$store.state.apiURL}clients/${this.factura.header.client_id}`, config)
            .then((result) => {
                const headerData = result.data;
                if (!headerData || !headerData.id) {
                    // Si el cliente no existe (e.g., CF), no hacemos nada
                    return;
                }
                self.mapClientToHeader(headerData);
            })
            .catch(error => {
                console.error("Error al cargar datos del cliente:", error.response || error);
                // Aquí podrías mostrar una advertencia si no es CF y falla.
            });
        },
        mapClientToHeader(client) {
            // Mapeo de datos del cliente al encabezado de la factura (para tenerlos a mano)
            this.factura.header.client_name = client?.name || 'Consumidor Final';
            this.factura.header.client_nit = client?.nit || '0000000000';
            this.factura.header.client_address = client?.fiscal_address || client?.commercial_address || 'Sin dirección';
            this.factura.header.client_nrc = client?.nrc || null;
            this.factura.header.client_document_type = client?.document_type || '36';
            this.factura.header.client_email = client?.email || null;
            this.factura.header.client_phone=client?.phone || null;
            this.factura.header.excent=client?.excent || null;
        },
        async fetchInvoice(config){
            const self = this;
            const invoiceId = this.$route.params.id;

            if (!invoiceId) {
                self.factura = null;
                return;
            }

            axios.get(`${this.$store.state.apiURL}invoice_headers/${invoiceId}?include_lines=true`, config)
            .then((result) => {
                const headerData = result.data;
                if (!headerData || !headerData.id) {
                    throw new Error('Factura no encontrada o datos incompletos.');
                }

                // CONVERSIÓN DE TIPOS EN LAS LÍNEAS
                const processedLines = (headerData.lines || []).map(line => ({
                    ...line,
                    quantity: parseFloat(line.quantity),
                    unit_price: parseFloat(line.unit_price),
                    discount: parseFloat(line.discount),
                    tax_rate: parseFloat(line.tax_rate),
                    line_total: parseFloat(line.line_total),
                }));

                // CONVERSIÓN DE TIPOS EN EL ENCABEZADO
                const processedHeader = {
                    ...headerData,
                    sub_total_brut: parseFloat(headerData.sub_total_brut),
                    subtotal: parseFloat(headerData.subtotal),
                    total_taxes: parseFloat(headerData.total_taxes),
                    total_discount: parseFloat(headerData.total_discount),
                    retention_amount: parseFloat(headerData.retention_amount),
                    total: parseFloat(headerData.total),
                };

                self.factura = {
                    header: processedHeader,
                    lines: processedLines
                };

                self.isLoading=false;
                self.fecthCustomer(config);
            })
            .catch(error => {
                console.error("Error al cargar la factura:", error.response || error);
                self.factura = null;
                self.isLoading=false;

                if (error.response && error.response.status === 401) {
                    self.$swal({ /* ... (lógica de expiración de sesión) ... */ });
                    // Redirección si la sesión expira
                    self.$router.push('/signin');
                } else {
                    self.$swal({
                        title: 'Error de Carga',
                        text: 'Ocurrió un error al intentar cargar la factura. Revise la consola.',
                        icon: 'error'
                    });
                }
            });
        },
        getStateClass(state) {
            // Estilos CSS para los estados (mantenido del código original)
            return state === 'EMITTED' ? 'text-warning' : state === 'PAID' ? 'text-success' : 'text-danger';
        },
        getStateText(state) {
            // Texto descriptivo para los estados (mantenido del código original)
            return state === 'EMITTED' ? 'Emitida' : state === 'PAID' ? 'Pagada' : 'Cancelada';
        },
        goBack() {
            this.$router.push('/invoices');
        },
        async printPDF() {
            // ... (Lógica de impresión, se mantiene) ...
            if (!this.factura || this.factura.lines.length === 0) {
                alert('No hay datos de factura para imprimir.');
                return;
            }
            console.log(this.factura.header.invoice_type);

            let formatToUse = this.companyConfig.invoice_default_format
            if(this.factura.header.invoice_type=='TICKET'){
                useInvoicePrinter(this.factura,this.companyConfig, true);
            }else{
                if (formatToUse == 3 || (formatToUse != 1 && formatToUse != 2)) {
                    const selection = await this.askForInvoiceFormat();
                    formatToUse = selection;
                }
                if (formatToUse == 1) {
                    useInvoicePrinter(this.factura, this.companyConfig, true);
                } else if (formatToUse == 2) {
                    useDTEFiscal(this.factura, this.companyConfig, true);
                } else {
                    this.$swal('Impresión Cancelada', 'No se seleccionó un formato válido.', 'info');
                }

            }
        },
        async askForInvoiceFormat() {
            // ... (Lógica para preguntar formato de impresión, se mantiene) ...
            const result = await this.$swal({
                title: 'Seleccione Formato de Impresión',
                text: '¿Qué formato desea utilizar para este documento?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Formato Carta (Legible)',
                cancelButtonText: 'Formato Ticket (POS)',
                reverseButtons: true,
                focusConfirm: false,
                showDenyButton: false,
                confirmButtonColor: '#17c1e8',
                cancelButtonColor: '#2dce89',
            });

            if (result.isConfirmed) {
                return 2;
            }
            else if (result.dismiss === this.$swal.DismissReason.cancel) {
                return 1;
            }
            return 0;
        },

        // 🛑 LÓGICA DE ANULACIÓN 🛑

        // Función principal para iniciar el flujo de anulación
        async handleInvalidationFlow() {
            if (!this.canInvalidate) {
                this.$swal('Invalidez', 'Este documento no puede ser anulado en su estado actual.', 'warning');
                return;
            }

            // 1. Preguntar por el motivo y tipo de anulación
            const { value: formValues } = await this.$swal({
                title: 'Confirmar Anulación DTE',
                html:
                    '<p class="text-sm text-start">Esta acción no se puede deshacer y registrará una solicitud de anulación ante el Ministerio de Hacienda.</p>' +
                    '<label for="swal-input2" class="text-start w-100 mt-3 mb-1">Motivo (Máx. 250 caracteres):</label>' +
                    '<textarea id="swal-input2" class="swal2-textarea  w-80 " placeholder="Describa la razón de la anulación..." maxlength="250"></textarea>',
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: 'Sí, Anular Documento',
                cancelButtonText: 'Cancelar',
                preConfirm: () => {
                    const tipoAnulacion = 2;
                    const motivoAnulacion = document.getElementById('swal-input2').value;
                    if (!motivoAnulacion || motivoAnulacion.length < 5) {
                        this.$swal.showValidationMessage('Debe ingresar un motivo de anulación válido (mínimo 5 caracteres).');
                        return false;
                    }
                    return { tipo: tipoAnulacion, motivo: motivoAnulacion };
                }
            });

            if (formValues) {
                this.sendInvalidationRequest(formValues);
            }
        },

        // 2. Implementación de la llamada a la API simplificada
        async sendInvalidationRequest(formValues) {
            this.isSendingInvalidation = true;
            const API_ENDPOINT = `${this.$store.state.apiURL}invoices/invalidation`;

            if (!this.currentUser || !this.currentUser.nit || !this.factura) {
                this.$swal('Error', 'No se pudieron obtener los datos de usuario o factura para la anulación.', 'error');
                this.isSendingInvalidation = false;
                return;
            }

            // Construcción del payload simplificado (solo IDs y Motivo)
            const payload = {
                company_id: this.factura.header.company,
                invoice_id: this.factura.header.id,

                motivo: {
                    tipoAnulacion: parseInt(formValues.tipo),
                    motivoAnulacion: formValues.motivo,

                    // Datos del responsable/solicitante (usando el usuario logueado)
                    nombreResponsable: this.currentUser.fullName || this.currentUser.username,
                    tipDocResponsable: '36',
                    numDocResponsable: this.currentUser.nit,
                    nombreSolicita: this.currentUser.fullName || this.currentUser.username,
                    tipDocSolicita: '36',
                    numDocSolicita: this.currentUser.nit,
                }
            };

            try {
                const token = localStorage.getItem("accessToken");
                const response = await axios.post(API_ENDPOINT, payload, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });

                this.$swal('Solicitud Enviada',
                           `El DTE ha sido enviado a Hacienda para su anulación. Código de Solicitud: ${response.data.codigo_generacion_solicitud}.`,
                           'success');

                // Actualizar estado DTE a 'PENDIENTE_ANULACION' para reflejar el envío
                this.factura.header.dte_status = 'PENDIENTE_ANULACION';
                this.fetchInvoice({ headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });

            } catch (error) {
                const errorMessage = error.response?.data?.error || 'Error desconocido al procesar la solicitud.';
                this.$swal('Error de Anulación', errorMessage, 'error');
            } finally {
                this.isSendingInvalidation = false;
            }
        }
    }
};
</script>